'use client'

// MUI Imports
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Fade from '@mui/material/Fade'
import { useTheme } from '@mui/material/styles'

// Component Imports
import IconifyIcon from '@/@core/components/icon'

// Hook Imports
import { useWeatherTranslations } from '@/hooks/useTranslations'

// Types
interface NoLocationStateProps {
  glassmorphismStyle: Record<string, any>
  onRequestLocation: () => void
}

const NoLocationState = ({ glassmorphismStyle, onRequestLocation }: NoLocationStateProps) => {
  const theme = useTheme()
  const { t } = useWeatherTranslations()

    return (
    <Fade in timeout={500}>
      <Card
        elevation={0}
        sx={{
          ...glassmorphismStyle,
          width: { xs: '100%', sm: 'fit-content' },
          maxWidth: '100%'
        }}
      >
        <CardContent
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            width: { xs: '100%', sm: 'fit-content' },
            minWidth: 'fit-content',
            '&:last-child': { pb: 2 }
          }}
        >
          <Stack direction='row' alignItems='center' spacing={2} sx={{ width: '100%' }}>
            {/* Ícone com background gradiente */}
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #64B5F6 0%, #42A5F5 100%)'
                  : 'linear-gradient(135deg, #42A5F5 0%, #1E88E5 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 4px 12px rgba(100, 181, 246, 0.25)'
                  : '0 4px 12px rgba(30, 136, 229, 0.25)',
                flexShrink: 0
              }}
            >
              <IconifyIcon
                icon='ri:map-pin-line'
                fontSize='1.25rem'
                color='white'
              />
            </Box>

            {/* Textos */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant='body2'
                color='text.primary'
                sx={{
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  lineHeight: 1.3,
                  mb: 0.25
                }}
              >
                {t('noLocation.title')}
              </Typography>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{
                  fontSize: '0.75rem',
                  lineHeight: 1.2,
                  opacity: 0.8,
                  display: { xs: 'none', sm: 'block' }
                }}
              >
                {t('noLocation.message')}
              </Typography>
            </Box>

            {/* Botão */}
            <Button
              variant='contained'
              size='small'
              onClick={onRequestLocation}
              startIcon={<IconifyIcon icon='ri:map-pin-add-line' fontSize='1rem' />}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 500,
                px: 2,
                py: 0.75,
                fontSize: '0.75rem',
                flexShrink: 0,
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #64B5F6 0%, #42A5F5 100%)'
                  : 'linear-gradient(135deg, #42A5F5 0%, #1E88E5 100%)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 2px 8px rgba(100, 181, 246, 0.3)'
                  : '0 2px 8px rgba(30, 136, 229, 0.3)',
                '&:hover': {
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #90CAF9 0%, #64B5F6 100%)'
                    : 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 4px 12px rgba(100, 181, 246, 0.4)'
                    : '0 4px 12px rgba(30, 136, 229, 0.4)',
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              {t('noLocation.button')}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Fade>
  )
}

export default NoLocationState
